// Attachments tab of the order detail modal (admin Orders page): upload,
// list, download, delete files attached to an order (e.g. a shipping-label
// PDF). Dispatch screens will surface these later — this component is the
// only writer.
//
// Downloads go through apiFetch (session cookie + Bearer replay) and are
// saved via a blob object-URL — a plain <a href> to the BE would arrive
// without credentials on cross-site staging.
import { useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, Tag, Trash2, Upload } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useReadOnly } from "../../access/ReadOnly";

// The one typed kind the dispatch flow cares about: an attachment marked
// as the shipping label is offered at Confirm Shipment as an alternative to
// generating one via a courier API. Everything else stays 'other'.
export const SHIPPING_LABEL_KIND = "shipping_label";

export interface OrderAttachment {
    attachment_id: string;
    filename: string;
    mime_type: string;
    size_bytes: number;
    kind: string;
    uploaded_by_name: string | null;
    created_at: string;
}

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.xlsx,.csv";
const MAX_BYTES = 10 * 1024 * 1024;

const formatSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
};

const formatTs = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const OrderAttachments = ({
    orderId,
    t,
}: {
    orderId: string;
    t: (k: string, p?: Record<string, string | number>) => string;
}) => {
    const readOnly = useReadOnly();
    const [attachments, setAttachments] = useState<OrderAttachment[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null); // "upload" | attachment_id
    // When ticked, the next uploaded file is tagged kind='shipping_label' so
    // the dispatch desk can use it as the label instead of a courier one.
    const [asShippingLabel, setAsShippingLabel] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const load = async (alive?: () => boolean) => {
        try {
            const r = await apiFetch(`/orders/${encodeURIComponent(orderId)}/attachments`, {});
            if (!r.ok || (alive && !alive())) return;
            const data: { attachments: OrderAttachment[] } = await r.json();
            if (alive && !alive()) return;
            setAttachments(Array.isArray(data.attachments) ? data.attachments : []);
        } catch {
            if (!alive || alive()) toast.error(t("server_error"), { toastId: "attach-load-err" });
        }
    };

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        load(() => mounted).finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const onUpload = async (file: File) => {
        if (readOnly) return;
        if (file.size > MAX_BYTES) {
            toast.error(t("attachments_too_large"));
            return;
        }
        setBusy("upload");
        try {
            const form = new FormData();
            form.append("file", file);
            if (asShippingLabel) form.append("kind", SHIPPING_LABEL_KIND);
            const r = await apiFetch(`/orders/${encodeURIComponent(orderId)}/attachments`, {
                method: "POST",
                body: form,
            });
            if (!r.ok) {
                const body = await r.json().catch(() => ({}));
                toast.error(body?.message || t("server_error"), { toastId: "attach-upload-err" });
                return;
            }
            toast.success(t("attachments_uploaded"));
            await load();
        } catch {
            toast.error(t("server_error"), { toastId: "attach-upload-err" });
        } finally {
            setBusy(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onDownload = async (a: OrderAttachment) => {
        setBusy(a.attachment_id);
        try {
            const r = await apiFetch(
                `/orders/${encodeURIComponent(orderId)}/attachments/${encodeURIComponent(a.attachment_id)}/download`,
                {},
            );
            if (!r.ok) {
                // 404 here means the DB row survived but the file is gone from
                // storage — surface that distinctly so it doesn't read as a
                // generic server blip (the failure mode when DATA_DIR was
                // misconfigured and a deploy wiped the ephemeral disk).
                const msg = r.status === 404
                    ? t("attachments_file_missing")
                    : t("server_error");
                toast.error(msg, { toastId: "attach-dl-err" });
                return;
            }
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = a.filename || "attachment";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t("server_error"), { toastId: "attach-dl-err" });
        } finally {
            setBusy(null);
        }
    };

    const onDelete = async (a: OrderAttachment) => {
        if (readOnly) return;
        if (!window.confirm(t("attachments_delete_confirm", { filename: a.filename }))) return;
        setBusy(a.attachment_id);
        try {
            const r = await apiFetch(
                `/orders/${encodeURIComponent(orderId)}/attachments/${encodeURIComponent(a.attachment_id)}`,
                { method: "DELETE" },
            );
            if (!r.ok) {
                toast.error(t("server_error"), { toastId: "attach-del-err" });
                return;
            }
            setAttachments(prev => prev.filter(x => x.attachment_id !== a.attachment_id));
        } catch {
            toast.error(t("server_error"), { toastId: "attach-del-err" });
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="order-attachments">
            <div className="order-attachments-toolbar">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT}
                    style={{ display: "none" }}
                    onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) void onUpload(f);
                    }}
                />
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={busy !== null || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                    onClick={() => fileInputRef.current?.click()}
                >
                    {busy === "upload"
                        ? <Loader2 size={14} className="spin" />
                        : <Upload size={14} />}
                    {t("attachments_upload")}
                </button>
                <label className="order-attachments-label-toggle">
                    <input
                        type="checkbox"
                        checked={asShippingLabel}
                        disabled={busy !== null || readOnly}
                        onChange={e => setAsShippingLabel(e.target.checked)}
                    />
                    {t("attachments_mark_shipping_label")}
                </label>
                <span className="order-attachments-hint">{t("attachments_hint")}</span>
            </div>

            {loading ? (
                <div className="locations-table-empty">{t("loading")}</div>
            ) : attachments.length === 0 ? (
                <div className="locations-table-empty">{t("attachments_empty")}</div>
            ) : (
                <div className="locations-table-wrap">
                    <table className="locations-table">
                        <thead>
                            <tr>
                                <th>{t("attachments_col_file")}</th>
                                <th>{t("attachments_col_size")}</th>
                                <th>{t("attachments_col_uploaded_by")}</th>
                                <th>{t("attachments_col_date")}</th>
                                <th aria-label={t("attachments_col_actions")} />
                            </tr>
                        </thead>
                        <tbody>
                            {attachments.map(a => (
                                <tr key={a.attachment_id}>
                                    <td className="order-attachments-name">
                                        <FileText size={15} />
                                        <span>{a.filename}</span>
                                        {a.kind === SHIPPING_LABEL_KIND && (
                                            <span className="order-attachments-label-badge">
                                                <Tag size={11} />
                                                {t("attachments_shipping_label_badge")}
                                            </span>
                                        )}
                                    </td>
                                    <td>{formatSize(a.size_bytes)}</td>
                                    <td>{a.uploaded_by_name ?? "—"}</td>
                                    <td>{formatTs(a.created_at)}</td>
                                    <td className="order-attachments-actions">
                                        <button
                                            type="button"
                                            className="btn btn-secondary order-attachments-download"
                                            aria-label={t("attachments_download")}
                                            disabled={busy !== null}
                                            onClick={() => void onDownload(a)}
                                        >
                                            {busy === a.attachment_id
                                                ? <Loader2 size={15} className="spin" />
                                                : <Download size={15} />}
                                            <span>{t("attachments_download")}</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn order-attachments-delete"
                                            aria-label={t("attachments_delete")}
                                            disabled={busy !== null || readOnly}
                                            aria-disabled={readOnly}
                                            title={readOnly ? t("read_only_mode") : undefined}
                                            onClick={() => void onDelete(a)}
                                        >
                                            <Trash2 size={15} />
                                            <span>{t("attachments_delete")}</span>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default OrderAttachments;
